import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  getMinutes,
  convertToTwelveHour,
} from '../../utils/TimeUtil';
import VibeTooltip from '../VibeTooltip/VibeTooltip';
import VibeIcon from '../VibeIcon/VibeIcon';
import viTime from '../../icons/viTime';
import color from '../../sass/color.scss';
import './TimeField.scss';

// hours & minutes in an array for the time picker dropdown
const hours = Array.from(Array(24).keys());
const minutes = Array.from(Array(60).keys());

// track the number of key presses for the hour/minute field
let isFirstKeyHour = true;
let isFirstKeyMinute = true;

function TimeField({
  className,
  style,
  time,
  min,
  max,
  midnightNextDay,
  tabIndex,
  disabled,
  onChange,
}) {
  // is the field active (focused in any way)
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  const inputRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const showNextDayLabel = midnightNextDay && (time === '00:00' || time === '24:00');

  // hour/minute from the time field
  const hour = (midnightNextDay && time === '00:00') || time === '24:00'
    // show 00 for the hour when 24 is used to symbolize midnight of the next day
    ? '00'
    : time.split(':')[0];
  const minute = time.split(':')[1];

  /**
   * Get the tooltip to show when hovering over the field
   */
  const getTimeTooltip = () => {
    return convertToTwelveHour({ time: `${hour}:${minute}` });
  };

  /**
   * When the user clicks away from the picker dropdown
   */
  const onClickAwayPopper = () => {
    setOpen(false);
    setFocus(false);
  };

  /**
   * When the user clicks anywhere in the field, set focus on the hour
   */
  const onClickField = (e) => {
    // focus unless containing a class to ignore this click or the field is disabled
    if (
      !disabled
      && e.target
      && !e.target.classList.contains('ignore-field-click')
    ) {
      hourRef.current.focus();
    }
  };

  /**
   * When the user clicks the icon to open the time picker
   */
  const onClickIcon = (e) => {
    // prevent bubbling the click event back to "onClickField"
    e.stopPropagation();

    if (disabled) {
      return;
    }

    setOpen(true);
    setFocus(true);
  };

  /**
   * When either hour/minute is changed, send the onChange to the parent
   */
  const onChangeTime = (newTime) => {
    let hasError = false;

    // use 24:00 to symbolize midnight of the next day
    if (midnightNextDay && newTime === '00:00') {
      newTime = '24:00';
    }

    const currMinutes = getMinutes(newTime);
    const minMinutes = getMinutes(min);
    const maxMinutes = getMinutes(max);

    if (max && currMinutes > maxMinutes) {
      // The new time cannot be more than the maximum time
      console.error('Time cannot exceed maximum of', max);
      hasError = true;
    } else if (min && currMinutes < minMinutes) {
      // The new time cannot be less than the minimum time
      console.error('Time cannot exceed minimum of', min);
      hasError = true;
    }

    setError(hasError);

    onChange({
      time24: newTime,
      hasError,
    });
  };

  /**
   * When user presses a key in a focused hour field
   */
  const onKeyDownHour = (e) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      // reset the value
      e.currentTarget.textContent = '00';
      return;
    }

    if (e.key === 'Tab' || e.key === 'ArrowRight') {
      // move to the minute field
      minuteRef.current.focus();
      return;
    }

    const numKey = toNumber(e.key);

    // do not allow keys that are not numbers
    if (Number.isNaN(numKey)) {
      return;
    }

    if (isFirstKeyHour) {
      // first key press in the hour field
      // set the hour with a prefixed 0
      const strHour = `0${e.key}`;

      // set the hour
      e.currentTarget.textContent = strHour;

      if (numKey > 2) {
        // anything higher than 2 on the first key press means the hour is set
        // automatically move on to the minute selection
        minuteRef.current.focus();
      }

      // no longer the first key press for the hour after this point
      isFirstKeyHour = false;
    } else {
      // second (or more) key press in the hour field
      // append the number to the one from the first key press (convert to a number first to remove prefixed 0's)
      const numHour = toNumber(`${e.currentTarget.textContent}${e.key}`);
      const strHour = toString(numHour < 10 ? `0${numHour}` : numHour);

      if (numHour > 23) {
        // hour can only be between 0-23
        return;
      }

      // change the hour shown on the field (state is updated onBlur)
      e.currentTarget.textContent = strHour;

      // automatically move on to the minute selection
      minuteRef.current.focus();
    }
  };

  /**
   * When user presses a key in a focused minute field
   */
  const onKeyDownMinute = (e) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      // reset the value
      e.currentTarget.textContent = '00';
      return;
    }

    if (e.key === 'Tab') {
      // blur from the minute field
      e.currentTarget.blur();
      return;
    }

    if (e.key === 'ArrowLeft') {
      // move to the hour field
      hourRef.current.focus();
    }

    const numKey = toNumber(e.key);

    // do not allow keys that are not numbers
    if (Number.isNaN(numKey)) {
      return;
    }

    if (isFirstKeyMinute) {
      // first key press in the minute field
      // set the minute with a prefixed 0
      const strMinute = `0${e.key}`;

      // set the minute
      e.currentTarget.textContent = strMinute;

      if (numKey > 5) {
        // anything higher than 5 on the first key press means the minute is set
        // automatically assume the user is done setting the time and blur the field
        e.currentTarget.blur();
      }

      // no longer the first key press for the minute after this point
      isFirstKeyMinute = false;
    } else {
      // second (or more) key press in the minute field
      // append the number to the one from the first key press (convert to a number first to remove prefixed 0's)
      const numMinute = toNumber(`${e.currentTarget.textContent}${e.key}`);
      const strMinute = toString(numMinute < 10 ? `0${numMinute}` : numMinute);

      if (numMinute > 59) {
        // minute can only be between 0-59
        return;
      }

      // change the minute shown on the field (state is updated onBlur)
      e.currentTarget.textContent = strMinute;
      e.currentTarget.blur();
    }
  };

  const onFocusHour = () => {
    if (disabled) {
      return;
    }

    setFocus(true);

    // reset the keys pressed for the hour field
    isFirstKeyHour = true;
  };

  const onBlurHour = (e) => {
    if (disabled) {
      return;
    }

    // set the hour
    onChangeTime(`${e.currentTarget.textContent}:${minuteRef.current.textContent}`);
    setFocus(false);

    // reset the keys pressed for the hour field
    isFirstKeyHour = true;
  };

  const onFocusMinute = () => {
    if (disabled) {
      return;
    }

    setFocus(true);

    // reset the keys pressed for the minute field
    isFirstKeyMinute = true;
  };

  const onBlurMinute = (e) => {
    if (disabled) {
      return;
    }

    // set the minute
    onChangeTime(`${hourRef.current.textContent}:${e.currentTarget.textContent}`);
    setFocus(false);

    // reset the keys pressed for the minute field
    isFirstKeyMinute = true;
  };

  return (
    <div
      ref={inputRef}
      className={classNames('TimeField', className, {
        focus,
        disabled,
        open,
        error,
      })}
      style={style}
      onClick={onClickField}
    >
      <VibeTooltip
        title={getTimeTooltip()}
        placement="top"
        enterDelay={200}
      >
        <div className="container">
          <div className="time-section">
            <span
              ref={hourRef}
              className={classNames('time-group', 'ignore-field-click', { disabled })}
              tabIndex={tabIndex}
              spellCheck="false"
              autoCapitalize="off"
              autoCorrect="off"
              inputMode="numeric"
              onKeyDown={onKeyDownHour}
              onFocus={onFocusHour}
              onBlur={onBlurHour}
              contentEditable={!disabled}
              suppressContentEditableWarning
            >
              {hour}
            </span>

            :

            <span
              ref={minuteRef}
              className={classNames('time-group', 'ignore-field-click', { disabled })}
              tabIndex={tabIndex}
              spellCheck="false"
              autoCapitalize="off"
              autoCorrect="off"
              inputMode="numeric"
              onKeyDown={onKeyDownMinute}
              onFocus={onFocusMinute}
              onBlur={onBlurMinute}
              contentEditable={!disabled}
              suppressContentEditableWarning
            >
              {minute}
            </span>

            {showNextDayLabel && (
              <div className="midnight-next-day">
                (Next Day)
              </div>
            )}
          </div>

          <div className="time-icon">
            <VibeIcon
              className="ignore-field-click"
              style={{
                alignSelf: 'flex-end',
              }}
              icon={viTime}
              color={color.primary}
              hoverColor={color.primary40}
              size={16}
              disabled={disabled}
              onClick={onClickIcon}
            />
          </div>
        </div>
      </VibeTooltip>

      <ClickAwayListener onClickAway={onClickAwayPopper}>
        <Popper
          className="TimeFieldPopper"
          anchorEl={inputRef.current}
          open={open}
          placement="bottom-start"
          style={{
            width: get(inputRef, 'current.offsetWidth', 350),
            zIndex: 1302,
            opacity: 1,
          }}
        >
          <div className="popper-root">
            <div className="popper-content">
              <div className="list list-hours">
                {hours.map(hour => {
                  // convert to string, and prefix with a 0 if needed
                  const useHour = hour < 10
                    ? `0${hour}`
                    : `${hour}`;

                  return (
                    <VibeTooltip
                      key={`hour-${useHour}`}
                      title={convertToTwelveHour({ time: `${useHour}:00`, short: true })}
                      placement="left"
                      enterDelay={200}
                    >
                      <div
                        className="list-item ignore-field-click"
                        onClick={() => onChangeTime(`${useHour}:${minute}`)}
                      >
                        {useHour}
                      </div>
                    </VibeTooltip>
                  );
                })}
              </div>

              <div className="list list-minutes">
                {minutes.map(minute => {
                  // convert to string, and prefix with a 0 if needed
                  const useMinute = minute < 10
                    ? `0${minute}`
                    : `${minute}`;

                  return (
                    <div
                      key={`minute-${minute}`}
                      className="list-item ignore-field-click"
                      onClick={() => onChangeTime(`${hour}:${useMinute}`)}
                    >
                      {useMinute}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Popper>
      </ClickAwayListener>
    </div>
  );
}

TimeField.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  time: PropTypes.string.isRequired,
  min: PropTypes.string,
  max: PropTypes.string,
  /** Show the (Next Day) label for Midnight */
  midnightNextDay: PropTypes.bool,
  tabIndex: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

TimeField.defaultProps = {
  className: '',
  style: {},
  min: '',
  max: '',
  midnightNextDay: false,
  tabIndex: -1,
  disabled: false,
  onChange: () => {},
};

export default TimeField;
